/**
 * A pilot's detail page (F4).
 *
 * The KPI table is the centrepiece — PRD §5.4 calls it "the single most
 * important field group in the product" — so it sits at the top of the main
 * column rather than behind a tab. Everything else is arranged by how often
 * someone acts on it: milestones and blockers below it, and the reference
 * facts (budget, dates, consents) in the sidebar.
 *
 * No tabs. The startup detail page has six sections and needs them; a pilot has
 * four, all of which a person reviewing one wants at once — hiding the blockers
 * behind a tab is how a blocked pilot goes unnoticed in a weekly review.
 *
 * A 409 gets the conflict dialog rather than an inline retry: "try again" is
 * wrong advice when a colleague has already saved, and the remedy is to reload
 * and see what they changed (PRD §6.2).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { ConflictDialog } from '../../components/domain/ConflictDialog';
import { DefinitionList } from '../../components/domain/DefinitionList';
import { Money } from '../../components/domain/Money';
import { PilotStatusBadge } from '../../components/domain/PilotStatusBadge';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Grid, Stack } from '../../components/ui/Grid';
import { Kicker } from '../../components/ui/Kicker';
import { PageHead } from '../../components/ui/PageHead';
import { useAuth } from '../../lib/auth-context';
import { formatDate, formatNumber } from '../../lib/format';
import { ROUTES } from '../../lib/routes';
import { useConflict } from '../../lib/use-conflict';

import {
  useAddBlocker,
  usePilot,
  useReplaceKpis,
  useReplaceMilestones,
  useResolveBlocker,
} from './api/pilots.queries';
import { BlockerList } from './components/BlockerList';
import { ConsentChecklist } from './components/ConsentChecklist';
import { draftsToPayload } from './components/kpi-math';
import { KpiTable } from './components/KpiTable';
import { milestoneDraftsToPayload } from './components/milestone-list';
import { MilestoneList } from './components/MilestoneList';

export function PilotDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();

  const { data, isPending, isError, refetch } = usePilot(id);
  const { conflict, reportIfConflict, clear: clearConflict } = useConflict();

  const replaceKpis = useReplaceKpis(id);
  const replaceMilestones = useReplaceMilestones(id);
  const addBlocker = useAddBlocker(id);
  const resolveBlocker = useResolveBlocker(id);

  if (isPending) {
    return (
      <p role="status" className="text-body">
        {t('state.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <div>
        <p role="alert" className="text-sem-bad mb-s2">
          {t('pilot.notFound')}
        </p>
        <Link to={ROUTES.pilots} className="text-blue text-sm underline">
          {t('pilot.backToList')}
        </Link>
      </div>
    );
  }

  const canEdit = can('EDITOR');
  const host = data.customerName ?? data.assetName;
  const notSet = <span className="text-gray-400">{t('startup.notSet')}</span>;

  /** Every child mutation reports a 409 the same way. */
  const onError = (error: unknown): void => {
    reportIfConflict(error);
  };

  return (
    <>
      <nav aria-label={t('startup.breadcrumb')} className="text-body mb-s2 text-xs">
        {/* Persistently underlined, not only on hover: inside a text block a
            link distinguished by colour alone fails WCAG 1.4.1 (PRD §9.7). */}
        <Link to={ROUTES.pilots} className="text-blue underline">
          {t('pilot.title')}
        </Link>
        <span className="text-gray-200 mx-1.5">/</span>
        <span>{data.title}</span>
      </nav>

      <PageHead
        title={data.title}
        /* The startup is the lead, not the pilot's own subtitle: a pilot is
           always "X's pilot at Y" in conversation. */
        lead={
          /* Resolved to a single value first. Written as
             `data.customerName ?? data.assetName === null` it parses as
             `customerName ?? (assetName === null)`, which is truthy whenever
             there IS a customer — so the no-host wording fired for hosted
             pilots. */
          host === null
            ? t('pilot.leadWithoutHost', { startup: data.startupName })
            : t('pilot.leadWithHost', { startup: data.startupName, host })
        }
        actions={
          <>
            <PilotStatusBadge status={data.status} />
            {data.isOverdue ? <Badge tone="bad">{t('pilot.overdue')}</Badge> : null}
          </>
        }
      />

      <Grid cols="detail">
        <Stack>
          {/* PRD §5.4's centrepiece, first and unhidden. */}
          <KpiTable
            kpis={data.kpis}
            canEdit={canEdit}
            isSaving={replaceKpis.isPending}
            onSave={(drafts) => {
              replaceKpis.mutate(
                { version: data.version, kpis: draftsToPayload(drafts) },
                { onError },
              );
            }}
          />

          <MilestoneList
            milestones={data.milestones}
            canEdit={canEdit}
            isSaving={replaceMilestones.isPending}
            onSave={(drafts) => {
              replaceMilestones.mutate(
                {
                  version: data.version,
                  milestones: milestoneDraftsToPayload(drafts),
                },
                { onError },
              );
            }}
          />

          <BlockerList
            blockers={data.blockers}
            canEdit={canEdit}
            isSaving={addBlocker.isPending || resolveBlocker.isPending}
            onAdd={(description) => {
              addBlocker.mutate({ version: data.version, description }, { onError });
            }}
            onSetResolved={(blockerId, resolved) => {
              resolveBlocker.mutate(
                { blockerId, resolved, version: data.version },
                { onError },
              );
            }}
          />
        </Stack>

        <Stack>
          <Card>
            <CardHead>
              <Kicker as="h2">{t('pilot.sections.schedule')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('pilot.fields.start'),
                    value: data.startDate === null ? notSet : formatDate(data.startDate),
                  },
                  {
                    label: t('pilot.fields.plannedEnd'),
                    value:
                      data.plannedEndDate === null
                        ? notSet
                        : formatDate(data.plannedEndDate),
                  },
                  {
                    label: t('pilot.fields.actualEnd'),
                    value:
                      data.actualEndDate === null
                        ? notSet
                        : formatDate(data.actualEndDate),
                  },
                ]}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHead>
              <Kicker as="h2">{t('pilot.sections.budget')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('pilot.fields.capex'),
                    /* Exact on a detail page, abbreviated in a table
                       (PRD §9.5) — the figure here IS the record. */
                    value:
                      data.capexEur === null ? (
                        notSet
                      ) : (
                        <Money value={data.capexEur} style="exact" />
                      ),
                  },
                  {
                    label: t('pilot.fields.opex'),
                    value:
                      data.opexEur === null ? (
                        notSet
                      ) : (
                        <Money value={data.opexEur} style="exact" />
                      ),
                  },
                  {
                    label: t('pilot.fields.effortDays'),
                    value:
                      data.internalEffortDays === null ? (
                        notSet
                      ) : (
                        <span className="tabular-nums">
                          {formatNumber(data.internalEffortDays)}
                        </span>
                      ),
                  },
                ]}
              />
              {/* PRD §3 excludes any approval workflow, and a budget figure
                  with no stated status invites the assumption there is one. */}
              <p className="text-body mt-s3 text-xs font-light">
                {t('pilot.fields.budgetNote')}
              </p>
            </CardBody>
          </Card>

          <ConsentChecklist pilot={data} />

          {/* Only once there is one. An empty outcome card on a live pilot
              reads as a missing field rather than as "not concluded yet". */}
          {data.outcome === null ? null : (
            <Card>
              <CardHead>
                <Kicker as="h2">{t('pilot.sections.outcome')}</Kicker>
              </CardHead>
              <CardBody>
                <DefinitionList
                  items={[
                    {
                      label: t('pilot.fields.outcome'),
                      value: t(`pilot.outcome.${data.outcome}`),
                    },
                    {
                      label: t('pilot.fields.decision'),
                      value:
                        data.decision === null
                          ? notSet
                          : t(`pilot.decision.${data.decision}`),
                    },
                    {
                      label: t('pilot.fields.lessons'),
                      value: data.lessonsLearned ?? notSet,
                    },
                  ]}
                />
              </CardBody>
            </Card>
          )}
        </Stack>
      </Grid>

      <ConflictDialog
        open={conflict !== null}
        submittedVersion={conflict?.submittedVersion}
        onDismiss={clearConflict}
        /* Refetches rather than reloading the page: the conflict is about one
           record, and a page reload would lose the rest of the user's
           context. */
        onReload={() => {
          clearConflict();
          void refetch();
        }}
      />
    </>
  );
}
